import { ChangeDetectorRef, Component, inject, Input, OnDestroy, OnInit, signal } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { AppState } from '@reducers/interfaces';
import { parseDate, toISODateString } from '@shiftbase-com/ts-shared/utilities';
import { isValid } from 'date-fns';
import { Observable, of as observableOf, Subscription } from 'rxjs';
import { catchError, debounceTime, filter, map, switchMap, tap } from 'rxjs/operators';

import { FormChange, formChange } from '../../../../../../forms/form.helper';
import { ValidationService } from '../../../../../../forms/validation.service';
import { CustomFieldsModel } from '../../../../../../reducers/orm/custom-fields/custom-fields.model';
import { DepartmentModel } from '../../../../../../reducers/orm/department/department.model';
import { isPaidBreaksEnabled } from '../../../../../../reducers/orm/department/paid-breaks.helper';
import { LocationModel } from '../../../../../../reducers/orm/location/location.model';
import { RateCardModel } from '../../../../../../reducers/orm/rate-card/rate-card.model';
import { ScheduleModel } from '../../../../../../reducers/orm/schedule/schedule.model';
import { ShiftModel } from '../../../../../../reducers/orm/shift/shift.model';
import { filterShiftOptionsPerDepartment } from '../../../../../../reducers/orm/shift/shift.service';
import { TeamModel } from '../../../../../../reducers/orm/team/team.model';
import { filterTeamOptionsPerDepartment } from '../../../../../../reducers/orm/team/team.service';
import { TimesheetModel } from '../../../../../../reducers/orm/timesheet/timesheet.model';
import { getTimesheet, TimesheetService } from '../../../../../../reducers/orm/timesheet/timesheet.service';
import {
  TimesheetColumns,
  TimesheetCustomFieldsColumns,
} from '../../../../../../reducers/page-filters/page-filters.model';
import { lazySelect } from '../../../../../../shared/lazy-select.observable';
import { TimesheetScheduleDeviations } from '../../../interfaces';
import { useAutosaveRowHold } from '../autosave-row-hold';

@Component({
  standalone: false,
  selector: 'timesheet-row-edit',
  templateUrl: './timesheet-row-edit.component.html',
})
export class TimesheetRowEditComponent implements OnInit, OnDestroy {
  private cd = inject(ChangeDetectorRef);
  private timesheetService = inject(TimesheetService);
  private store = inject<Store<AppState>>(Store);

  // Until every change has been checked and saved: unmounting the row cancels the checks, the debounce
  // and the request.
  #autosaveHold = useAutosaveRowHold();

  @Input()
  timesheet: TimesheetModel;
  @Input()
  schedule?: ScheduleModel;
  @Input()
  customFields: CustomFieldsModel[] = [];

  @Input()
  locations: { [id: string]: LocationModel };
  @Input()
  departments: { [id: string]: DepartmentModel };
  @Input()
  departmentOptions: [
    {
      location: LocationModel;
      departments: DepartmentModel[];
    },
  ];

  _teams: TeamModel[];
  @Input()
  set teams(teams: TeamModel[]) {
    this._teams = teams;
    this.setTeamOptions();
  }

  _shifts: ShiftModel[];
  @Input()
  set shifts(shifts: ShiftModel[]) {
    this._shifts = shifts;
    this.setShiftOptions();
  }

  rateCardOptions: { value?: string; label: string; deleted?: boolean; deletedDate: string }[];
  selectedRateCard: RateCardModel;

  @Input()
  set rateCards(value: RateCardModel[] | undefined) {
    const rateCards = value ?? [];

    this.selectedRateCard = rateCards.find((rateCard) => rateCard.id === this.timesheet.rate_card_id);
    this.rateCardOptions = rateCards
      .filter((rateCard) => {
        if (rateCard.id === this.timesheet.rate_card_id) return true;

        return !rateCard.deleted;
      })
      .map((rateCard) => {
        const deletedDate = parseDate(rateCard.deleted_date ?? '');

        return {
          value: rateCard.id,
          label: rateCard.name,
          deleted: rateCard.deleted,
          deletedDate: rateCard.deleted && isValid(deletedDate) ? toISODateString(deletedDate) : '',
        };
      });
  }

  @Input()
  absenceWarning: boolean;
  @Input()
  doubleRegistration: boolean;
  @Input()
  deviationsFromSchedule: TimesheetScheduleDeviations;

  @Input()
  cols: TimesheetColumns;
  @Input()
  customFieldCols: TimesheetCustomFieldsColumns;

  paidBreaksEnabled = signal(false);

  form = new UntypedFormGroup({
    id: new UntypedFormControl('', Validators.required),
    date: new UntypedFormControl('', { validators: [Validators.required], updateOn: 'blur' }),
    starttime: new UntypedFormControl('', {
      validators: [Validators.required, ValidationService.timeValidator],
      updateOn: 'blur',
    }),
    endtime: new UntypedFormControl('', {
      validators: [Validators.required, ValidationService.timeValidator],
      updateOn: 'blur',
    }),
    break: new UntypedFormControl('', {
      validators: [Validators.required, ValidationService.numberValidator],
      updateOn: 'blur',
    }),
    paid_break: new UntypedFormControl('', {
      validators: [Validators.required, ValidationService.numberValidator],
      updateOn: 'blur',
    }),
    kilometers: new UntypedFormControl('', {
      validators: [Validators.required, ValidationService.decimalValidator],
      updateOn: 'blur',
    }),
    meals: new UntypedFormControl('', {
      validators: [Validators.required, ValidationService.numberValidator],
      updateOn: 'blur',
    }),
    note: new UntypedFormControl('', { updateOn: 'blur' }),
    user_id: new UntypedFormControl('', Validators.required),
    department_id: new UntypedFormControl('', Validators.required),
    shift_id: new UntypedFormControl('', Validators.required),
    team_id: new UntypedFormControl('', Validators.required),
    rate_card_id: new UntypedFormControl('', Validators.required),
    status: new UntypedFormControl('', Validators.required),
  });

  selectedDepartment: DepartmentModel;
  selectedLocation: LocationModel;

  teamOptions: { value: string; label: string }[];
  shiftOptions: { value: string; label: string }[];

  private internalChange = false;
  private dataSubs = new Subscription();

  ngOnInit(): void {
    this.initForm();
    this.setDepartment(this.timesheet.department_id);

    this.dataSubs.add(
      this.store
        .pipe(
          lazySelect(getTimesheet(this.timesheet.id)),
          filter(() => !this.internalChange),
        )
        .subscribe((data) => {
          if (!data) {
            return;
          }
          this.form.patchValue(data, { emitEvent: false });

          this.cd.detectChanges();
        }),
    );
  }

  ngOnDestroy(): void {
    this.dataSubs.unsubscribe();
  }

  onNoteChange(value: string): void {
    this.form.controls['note'].setValue(value);
  }

  private initForm() {
    const formResetOptions = {
      onlySelf: true,
      emitEvent: false,
    };

    this.form.reset(this.timesheet, formResetOptions);

    this.dataSubs.add(
      formChange(this.form)
        .pipe(
          map((formChange) => ({ formChange, change: this.#autosaveHold.changed() })),
          switchMap(({ formChange, change }) =>
            this.checkChange(formChange).pipe(map((checked) => ({ checked, change }))),
          ),
          tap(({ checked, change }) => {
            if (checked === false) {
              this.#autosaveHold.rejected(change);
            } else {
              this.#autosaveHold.passed(change);
            }
          }),
          filter((item): item is { checked: FormChange; change: number } => item.checked !== false),
          debounceTime(200),
          tap(() => (this.internalChange = true)),
          switchMap(({ checked, change }) => this.#autosaveHold.save(change, this.saveTimesheet(checked))),
        )
        .subscribe(
          (data) => {
            this.cd.markForCheck();
            this.internalChange = false;
          },
          () => {
            this.internalChange = false;
            this.#autosaveHold.stopped();
          },
        ),
    );
  }

  private checkChange(formChange: FormChange): Observable<FormChange | boolean> {
    if (formChange.control === 'department_id') {
      return this.changeDepartment(formChange.controlValue);
    }

    if (formChange.control === 'starttime' || formChange.control === 'endtime') {
      return this.checkBreak(formChange);
    }

    if (formChange.control === 'shift_id') {
      return this.checkRateCard(formChange);
    }

    if (!formChange.formValid) {
      return observableOf(false);
    }

    return observableOf(formChange);
  }

  private checkBreak(formChange: FormChange): Observable<FormChange | boolean> {
    //check starttime and endtime values
    if (!this.form.controls['starttime'].valid || !this.form.controls['endtime'].valid) {
      return observableOf(false);
    }

    return this.timesheetService.checkBreak(formChange.formValue).pipe(
      catchError(() => observableOf({ break: false, paid_break: false })),
      map((response) => {
        const breakControl = this.form.controls['break'];
        const paidBreakControl = this.form.controls['paid_break'];

        const hasBreakChanges =
          response.break !== false &&
          response.break !== true &&
          parseInt(response.break, 10) !== parseInt(breakControl.value, 10);

        if (hasBreakChanges) {
          breakControl.setValue(response.break);
        }

        const hasPaidBreakChanges =
          this.paidBreaksEnabled() &&
          response.paid_break !== false &&
          response.paid_break !== true &&
          response.paid_break !== undefined &&
          parseInt(response.paid_break, 10) !== parseInt(paidBreakControl.value, 10);

        if (hasPaidBreakChanges) {
          paidBreakControl.setValue(response.paid_break);
        }

        const hasChanges = hasPaidBreakChanges || hasBreakChanges;

        // form was set so we do not continue this stream, a new stream will be created by set value
        return hasChanges ? false : formChange;
      }),
    );
  }

  private checkRateCard(formChange: FormChange): Observable<FormChange | boolean> {
    //check starttime and endtime values
    if (!this.form.controls['shift_id'].valid) {
      return observableOf(false);
    }

    return this.timesheetService.checkRateCard(formChange.formValue).pipe(
      catchError((response) => observableOf({ rate_card_id: false })),
      map((response) => {
        if (response.rate_card_id === false) {
          return formChange;
        }

        const control = this.form.controls['rate_card_id'];
        if (response.rate_card_id == control.value) {
          return formChange;
        }

        control.setValue(response.rate_card_id);
        return false;
      }),
    );
  }

  private saveTimesheet(formChange: FormChange) {
    if (!formChange.formValid) {
      return observableOf(false);
    }

    return this.timesheetService.save(formChange.formValue);
  }

  private changeDepartment(departmentId: string) {
    if (this.selectedDepartment.id === departmentId) {
      return observableOf(false);
    }

    this.form.controls['team_id'].setValue(null);
    this.form.controls['shift_id'].setValue(null);

    this.setDepartment(departmentId);
    this.cd.markForCheck();

    return observableOf(false);
  }

  private setDepartment(departmentId: string) {
    this.selectedDepartment = this.departments[departmentId];

    const paidBreaksEnabled = isPaidBreaksEnabled(this.selectedDepartment);
    this.paidBreaksEnabled.set(paidBreaksEnabled);

    // Disabled controls are excluded from validation and from form.value (the
    // saved payload), so the paid break is neither validated nor sent when the
    // selected department has paid breaks off. The template hides the column.
    const paidBreakControl = this.form.controls['paid_break'];
    if (paidBreaksEnabled) {
      paidBreakControl?.enable({ emitEvent: false });
    } else {
      paidBreakControl?.disable({ emitEvent: false });
    }

    if (!this.selectedDepartment) {
      return;
    }
    this.selectedLocation = this.locations[this.selectedDepartment.location_id];

    this.setTeamOptions();
    this.setShiftOptions();
  }

  private setTeamOptions() {
    if (!this.form.controls['department_id'].value || !this._teams) {
      return;
    }

    const teamFilter = filterTeamOptionsPerDepartment(
      this.form.controls['department_id'].value,
      this.timesheet.team_id,
    );

    this.teamOptions = teamFilter(this._teams).map((team) => ({ value: team.id, label: team.name }));

    const teamControl = this.form.controls['team_id'];

    //set team if only 1 option
    if (this.teamOptions.length === 1) {
      const teamToSelect = this.teamOptions[0].value;

      if (teamControl.value !== teamToSelect) {
        teamControl.setValue(teamToSelect);
      }
    }

    const ids = this.teamOptions.map((option) => option.value);

    if (this.teamOptions.length > 1 && ids.indexOf(teamControl.value) === -1) {
      teamControl.setValue('');
    }
  }

  private setShiftOptions() {
    if (!this.form.controls['department_id'].value || !this._shifts) {
      return;
    }

    const shiftFilter = filterShiftOptionsPerDepartment(
      this.form.controls['department_id'].value,
      this.timesheet.shift_id,
    );

    this.shiftOptions = shiftFilter(this._shifts).map((shift) => ({ value: shift.id, label: shift.long_name }));

    const shiftControl = this.form.controls['shift_id'];

    //set shift if only 1 option
    if (this.shiftOptions.length === 1) {
      const shiftToSelect = this.shiftOptions[0].value;

      if (shiftControl.value !== shiftToSelect) {
        shiftControl.setValue(shiftToSelect);
      }
    }

    const ids = this.shiftOptions.map((option) => option.value);

    if (this.shiftOptions.length > 1 && ids.indexOf(shiftControl.value) === -1) {
      shiftControl.setValue('');
    }
  }
}
